/* eslint-disable no-param-reassign */
const componentName = 'date-picker';

class DatePicker extends HTMLElement {

    elements = {
        startInput: this.querySelector(`.${componentName}__start-input`),
        endInput: this.querySelector(`.${componentName}__end-input`),

        details: this.querySelector('details'),
        summary: this.querySelector('summary[role="combobox"]'),
        labelInput: this.querySelector(`.${componentName}__display-input`),

        monthSelect: this.querySelector(`.${componentName}__month-select`),
        prevMonthButton: this.querySelector('button[name="prev-month"]'),
        nextMonthButton: this.querySelector('button[name="next-month"]'),

        table: this.querySelector('table'),
        tableHeaders: this.querySelectorAll('th'),
        dates: this.querySelectorAll('td'),
    };

    constants = Object.freeze({
        localDate: new Date(),
    });

    state = {
        locale: (() => {
            try {
                // Check if locale is valid
                Intl.getCanonicalLocales(document.documentElement.lang);
                return document.documentElement.lang;
            } catch {
                return 'en-US';
            }
        })(),
        currentMonth: new Date(Date.UTC(this.constants.localDate.getFullYear(), this.constants.localDate.getMonth())),
        firstSelect: true,
        wasChanged: false,
    };

    connectedCallback() {
        this.initTableHeaders();
        this.initMonthSelect();
        this.displayMonth();
        this.addEventListeners();
    }

    initTableHeaders = () => {
        this.elements.tableHeaders.forEach((th, i) => {
            const date = new Date(1970, 0, 4 + i); // 1970-01-04 has weekday = 0
            th.textContent = date.toLocaleDateString(this.state.locale, { weekday: 'narrow' });
            th.title = date.toLocaleDateString(this.state.locale, { weekday: 'long' });
        });
    };

    initMonthSelect = () => {
        for (let i = -48; i <= 24; i += 1) { // back 4 years, forward 2 years
            const thisMonth = new Date(Date.UTC(this.state.currentMonth.getUTCFullYear(), this.state.currentMonth.getUTCMonth() + i));

            const option = document.createElement('option');
            option.textContent = thisMonth.toLocaleDateString(this.state.locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
            option.value = String(thisMonth.valueOf());
            this.elements.monthSelect.append(option);
        }
    };

    displayMonth = () => {
        this.elements.monthSelect.value = this.state.currentMonth.valueOf();

        const year = this.state.currentMonth.getUTCFullYear();
        const month = this.state.currentMonth.getUTCMonth();
        const firstDayOfWeek = this.state.currentMonth.getUTCDay();

        this.elements.dates.forEach((date, i) => {
            const thisDate = new Date(Date.UTC(year, month, 1 - firstDayOfWeek + i));

            if (thisDate.getUTCMonth() !== month) {
                date.textContent = '';
                date.dataset.timestamp = '';
            } else {
                date.textContent = String(thisDate.getUTCDate());
                date.dataset.timestamp = String(thisDate.valueOf());
            }
        });

        this.updateSelection();
    };

    /** @returns {HTMLTableCellElement[]} Date elements that are valid for the current month */
    getCurrentMonthDates = () => [...this.elements.dates].filter(
        (date) => date.dataset.timestamp !== ''
    );

    /** @param {number} monthOffset */
    switchMonth = (monthOffset) => {
        this.state.currentMonth.setUTCMonth(this.state.currentMonth.getUTCMonth() + monthOffset);
        this.displayMonth();
    };

    /** @param {HTMLElement | EventTarget | null} target */
    selectDateElement = (target) => {
        const timestamp = Number(target?.dataset.timestamp || NaN);
        if (Number.isInteger(timestamp)) {
            if (this.state.firstSelect) {
                this.elements.startInput.valueAsNumber = timestamp;
                this.elements.endInput.valueAsNumber = timestamp;
                this.state.firstSelect = false;
            } else {
                if (timestamp < this.elements.startInput.valueAsNumber) {
                    this.elements.startInput.valueAsNumber = timestamp;
                } else {
                    this.elements.endInput.valueAsNumber = timestamp;
                }
                this.state.firstSelect = true;
                this.elements.details.open = false;
                window.setTimeout(() => this.elements.summary.focus());
            }
            this.state.wasChanged = true;
            this.updateSelection();
        }
    };

    updateSelection = () => {
        const startDate = this.elements.startInput.valueAsNumber;
        const endDate = this.elements.endInput.valueAsNumber;
        this.elements.dates.forEach((date) => {
            const timestamp = Number(date.dataset.timestamp);
            date.dataset.inRange = timestamp >= startDate && timestamp <= endDate;
            date.dataset.rangeStart = timestamp === startDate;
            date.dataset.rangeEnd = timestamp === endDate;
        });

        if (Number.isInteger(startDate)) {
            let rangeString = this.formatDayOfMonth(startDate);
            if (Number.isInteger(endDate) && endDate !== startDate) {
                rangeString += ` - ${this.formatDayOfMonth(endDate)}`;
            }
            this.elements.labelInput.value = rangeString;
        } else {
            this.elements.labelInput.value = '';
        }
    };

    /** @param {number} timestamp */
    formatDayOfMonth = (timestamp) => new Date(timestamp)
        .toLocaleDateString(this.state.locale, { dateStyle: 'medium', timeZone: 'UTC' });

    /**
     * @param {HTMLElement | EventTarget} current
     * @param {number} offset
     */
    shiftDateFocus = (current, offset) => {
        const currentMonthDates = this.getCurrentMonthDates();
        const currentIndex = currentMonthDates.findIndex((date) => date === current);
        if (currentIndex !== -1) {
            const newIndex = currentIndex + offset;
            if (newIndex < 0) {
                this.switchMonth(-1);
                const newMonthDates = this.getCurrentMonthDates();
                newMonthDates[newMonthDates.length + newIndex].focus();
            } else if (newIndex >= currentMonthDates.length) {
                this.switchMonth(+1);
                const newMonthDates = this.getCurrentMonthDates();
                newMonthDates[newIndex - currentMonthDates.length].focus();
            } else {
                currentMonthDates[newIndex].focus();
            }
        }
    };

    handleDetailsToggle = () => {
        if (this.elements.details.open) {
        // On open, focus the first selected date if present, otherwise the first filled date
            const focusDate = [...this.elements.dates].find((date) => date.dataset.rangeStart === 'true')
                ?? [...this.elements.dates].find((date) => date.dataset.timestamp !== '');
            focusDate.focus();
        } else if (this.state.wasChanged) {
            // On close, if the selection was changed, dispatch change event
            this.elements.startInput.dispatchEvent(new Event('change'));
        }

        // Reset state
        this.state.firstSelect = true;
        this.state.wasChanged = false;
    };

    /** @param {Event} ev */
    handleMonthSelect = (ev) => {
        const selectedValue = Number(ev.currentTarget?.value ?? NaN);
        if (Number.isInteger(selectedValue)) {
            this.state.currentMonth = new Date(selectedValue);
            this.displayMonth();
        }
    };

    /** @param {MouseEvent} ev */
    handleTableClick = (ev) => {
        this.selectDateElement(ev.target);
    };

    /** @param {KeyboardEvent} */
    handleTableKeydown = (ev) => {
        let isHandled = true;
        switch (ev.key) {
        case 'Enter':
        case ' ':
            this.selectDateElement(ev.target);
            break;
        case 'Escape':
            this.elements.details.open = false;
            window.setTimeout(() => this.elements.summary.focus());
            break;
        case 'ArrowRight':
            this.shiftDateFocus(ev.target, +1);
            break;
        case 'ArrowLeft':
            this.shiftDateFocus(ev.target, -1);
            break;
        case 'ArrowDown':
            this.shiftDateFocus(ev.target, +7);
            break;
        case 'ArrowUp':
            this.shiftDateFocus(ev.target, -7);
            break;
        default:
            isHandled = false;
            break;
        }
        if (isHandled) {
            ev.preventDefault();
        }
    };

    addEventListeners = () => {
        this.addEventListener('focusout', (ev) => {
            if (!this.contains(ev.relatedTarget)) {
                this.elements.details.open = false;
            }
        });

        this.elements.summary.addEventListener('keydown', (ev) => {
            // Open with arrow down
            if (ev.key === 'ArrowDown') {
                ev.preventDefault();
                this.elements.details.open = true;
            }
        });

        this.elements.details.addEventListener('toggle', this.handleDetailsToggle);
        this.elements.monthSelect.addEventListener('change', this.handleMonthSelect);
        this.elements.prevMonthButton.addEventListener('click', () => this.switchMonth(-1));
        this.elements.nextMonthButton.addEventListener('click', () => this.switchMonth(+1));
        this.elements.table.addEventListener('click', this.handleTableClick);
        this.elements.table.addEventListener('keydown', this.handleTableKeydown);

        // eslint-disable-next-line no-unused-expressions
        this.elements.startInput.form?.addEventListener('reset', () => window.setTimeout(this.updateSelection));
    };

    // Public methods (used by filter-shared.js)
    clearSelection = () => {
        this.elements.startInput.value = '';
        this.elements.endInput.value = '';
        this.updateSelection();
    };

    getLabel = () => this.elements.labelInput.value;
    getName = () => this.elements.startInput.name;
}

window.customElements.define(componentName, DatePicker);
